/**
 * VaccineTimelineDialog — modal launched from the Immunizations chart
 * tab's "Vaccine Chart" link. Renders the patient's Vaccination
 * Administration Record per Flutter `vaccination_chart_widget.dart`.
 *
 * Flutter ref:
 *   `patient_module/patient_chart/.../widgets/vaccination_chart_widget.dart`
 *   + `data/datamodels/vaccine_chart_model.dart`
 *
 * Categories ship with a fixed dose count (`categoryNoOfDoses`). The
 * UI renders that many dose-slot rows per category — empty slots show
 * as "(n.)" placeholders so a clinician sees missed doses at a glance.
 * "Other" and "Unknown" doses (records whose `durationQuantity.code`
 * doesn't match a slot ordinal) append after the numbered rows.
 *
 * Chrome (Flutter `vaccination_chart_widget.dart:293-403`): a thin top
 * bar with "Close" (left) and "Print" (right) text buttons, then the
 * printable region — patient + practice context row, a CENTERED
 * "Vaccination Administration Record" title, then the table. Print
 * captures the region BELOW the bar (Flutter's `RepaintBoundary`),
 * never the bar itself.
 *
 * Table columns: Vaccine / Brand Name / Date Given / Lot # (Expiry) /
 * Site / Signs / Remarks. Each category renders as one row in the outer
 * table with the per-dose data stacked vertically in its cells —
 * matches Flutter's nested-table approach.
 *
 * FHIR: aggregate over `Immunization R4` resources — the timeline is a
 * presentation contract, not a separate resource.
 */

import { useRef } from 'react';
import { Modal, Skeleton, printNode } from '@scripsteam/scrips-signal-ds';
import { usePatientVaccineChart } from '@/patients/services/use-patient-vaccine-chart';
import type {
  PatientImmunization,
  VaccinationTimelineCategory,
  VaccineChartResponse,
} from '@/types/patient-chart-api';
import type { Patient } from '@/types/patient-api';

export interface VaccineTimelineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: Patient | null | undefined;
}

export function VaccineTimelineDialog({
  open,
  onOpenChange,
  patient,
}: VaccineTimelineDialogProps) {
  const patientId = patient?.patientId ?? null;
  const { data, isLoading, isError } = usePatientVaccineChart(patientId, open);

  // The printable region — patient header + title + table, NOT the
  // Close/Print bar (mirrors Flutter's `RepaintBoundary` boundary).
  const printRef = useRef<HTMLDivElement>(null);

  const fullName = [patient?.firstName, patient?.middleName, patient?.lastName]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(' ');

  const handlePrint = () => {
    // Filename parity: "{fullNameNoSpaces}_VaccinationChart"
    // (Flutter `vaccination_chart_widget.dart:310`).
    printNode(printRef.current, {
      title: `${fullName.replace(/\s+/g, '') || 'patient'}_VaccinationChart`,
    });
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      // Invisible accessible name — the visible title (below, inside the
      // printable region) is Flutter parity content, not DS chrome. This
      // gives the dialog a real accessible name without rendering it twice.
      title={<span className="sr-only">Vaccination Administration Record</span>}
      size="full"
      className="p-0"
    >
        {/* Top bar — Close (left) / Print (right). Flutter
            `vaccination_chart_widget.dart:296-318`. */}
        <div data-vtd="bar" className="flex items-center justify-between px-4 h-12 shrink-0 border-b border-border-default">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-vtd="link"
            className="text-base font-semibold text-brand-primary hover:underline"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handlePrint}
            data-vtd="link"
            className="text-base font-semibold text-brand-primary hover:underline"
          >
            Print
          </button>
        </div>

        <div className="overflow-y-auto scrollbar-hide">
          <div ref={printRef} className="px-6 pb-6 pt-4">
            <VaccinePatientHeader patient={patient} fullName={fullName} chart={data} />

            <p data-vtd="title" className="block text-center text-md font-bold text-text-primary mt-2 mb-4">
              Vaccination Administration Record
            </p>

            {isLoading ? (
              <div className="space-y-3 mt-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : isError ? (
              <EmptyCard>
                Could not load the vaccine chart. Try again later.
              </EmptyCard>
            ) : (
              <TimelineTable timeline={data?.vaccinationTimeline ?? []} />
            )}
          </div>
        </div>
    </Modal>
  );
}

function VaccinePatientHeader({
  patient,
  fullName,
  chart,
}: {
  patient: Patient | null | undefined;
  // Patient.fullName isn't a typed wire field on the React Patient
  // interface — computed by the caller from first / middle / last
  // (Flutter parity) and reused for the print filename.
  fullName: string;
  chart: VaccineChartResponse | undefined;
}) {
  const practice = chart?.practiceResponse;
  // Flutter shows `genderName.name, age` (e.g. "Female, 27y"). Prefer
  // the wire age if present; fall back to a computed age from birthDate.
  const ageLabel =
    patient?.age != null && `${patient.age}`.trim()
      ? `${patient.age}`
      : formatAge(patient?.birthDate);
  return (
    <div data-vtd="head" className="flex items-start justify-between gap-6 pb-4 border-b border-border-default mb-4">
      <div className="min-w-0">
        <p className="text-md font-semibold text-brand-depth">
          {fullName || '—'}
        </p>
        <p className="text-sm text-text-secondary">
          {patient?.patientMrn ?? '—'}
        </p>
        <p className="text-sm text-text-secondary">
          {[patient?.genderName?.name, ageLabel].filter(Boolean).join(', ') ||
            '—'}
        </p>
      </div>
      <div className="text-right text-sm text-text-secondary min-w-0">
        <p className="text-md font-semibold text-brand-depth">
          {practice?.name?.trim() || '—'}
        </p>
        <p>{practice?.address?.trim() || '—'}</p>
        <p>{practice?.phone?.trim() || '—'}</p>
      </div>
    </div>
  );
}

function TimelineTable({
  timeline,
}: {
  timeline: VaccinationTimelineCategory[];
}) {
  if (timeline.length === 0) {
    return <EmptyCard>No vaccinations recorded for this patient.</EmptyCard>;
  }

  // Display order — Flutter sorts ascending by `categoryOrder`. Don't
  // mutate input; copy first.
  const sorted = [...timeline].sort(
    (a, b) => (a.categoryOrder ?? 0) - (b.categoryOrder ?? 0),
  );

  return (
    // Full gridlines (Flutter `TableBorder.all()`): collapse borders so
    // every cell is boxed, matching the printed Vaccination
    // Administration Record.
    //
    // `table-fixed` — all seven columns get exactly 1/7 of the width (Andrew,
    // 2026-07-29: "all the columns the same sizes", matching the printed record).
    //
    // Without it the browser's default `table-layout: auto` sizes each column
    // from its content, so the layout shifted per patient: a chart containing
    // "Diphtheria, Tetanus, Accellular Pertussis (DTap, Tdap)" widened the
    // Vaccine column and squeezed Site/Signs/Remarks, while a chart of short
    // names did the opposite. Equal columns make the grid stable across charts.
    <table data-vtd="table" className="w-full table-fixed text-sm border-collapse border border-border-default">
      <thead className="bg-grey-100">
        <tr className="text-left text-text-muted">
          <Th>Vaccine</Th>
          <Th>Brand Name</Th>
          <Th>Date Given</Th>
          <Th>Lot no. (Expiry)</Th>
          <Th>Site</Th>
          <Th>Signs</Th>
          <Th>Remarks</Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((category) => (
          <CategoryRow
            key={category.id ?? category.categoryName ?? 'unknown'}
            category={category}
          />
        ))}
      </tbody>
    </table>
  );
}

function CategoryRow({ category }: { category: VaccinationTimelineCategory }) {
  // Slot rows — one per expected dose, with the matching record (if any)
  // filled in. "Other" / "Unknown" records append after the numbered
  // slots. Flutter `vaccination_chart_widget.dart:170-266` builds the
  // exact same shape: it matches each record by `durationQuantity.code`
  // against the dose ordinals "1".."N" and the literals "Other" /
  // "Unknown", and **silently drops any record whose code matches none
  // of those**. We mirror that drop — an earlier defensive "Unspecified"
  // catch-all surfaced untagged/null-code records that Flutter never
  // shows, which flooded real charts with duplicate "Unspecified. <name>"
  // rows (e.g. Pentaxim). Parity wins; no invented React-idiom bucket.
  const records = (category.patientImmunization ?? []).filter(
    (r) => r.isDeleted !== true,
  );

  const isOther = category.categoryName === 'Other';

  const slots: Array<{ label: string; record: PatientImmunization | null }> = [];

  if (isOther) {
    // The "Other" bucket isn't dose-indexed; each record is its own row.
    if (records.length === 0) {
      slots.push({ label: '', record: null });
    } else {
      for (const r of records) {
        slots.push({ label: '', record: r });
      }
    }
  } else {
    const expected = category.categoryNoOfDoses ?? 0;
    for (let i = 1; i <= expected; i++) {
      const ordinal = String(i);
      const match =
        records.find((r) => r.durationQuantity?.code === ordinal) ?? null;
      slots.push({ label: `${i}.`, record: match });
    }
    // Append "Other" and "Unknown" dose codes that didn't match a slot.
    // Any other code (null / unrecognised) is dropped, exactly as Flutter.
    for (const tag of ['Other', 'Unknown'] as const) {
      const overflow = records.filter((r) => r.durationQuantity?.code === tag);
      for (const r of overflow) {
        slots.push({ label: `${tag}.`, record: r });
      }
    }
  }

  // A category with no expected doses and no records still shows one
  // empty row — "show all even if empty" — so the category name never
  // sits next to blank, borderless cells.
  if (slots.length === 0) {
    slots.push({ label: '1.', record: null });
  }

  // Dash rule (clinic, 2026-07-28): show the value; if empty, a '-' only when the
  // slot is a real NAMED dose (has a brand/vaccine display), otherwise blank —
  // empty dose slots and brand-less records stay fully empty.
  const cellText = (
    slot: { label: string; record: PatientImmunization | null },
    value: string,
  ): string =>
    value && value.trim() !== ''
      ? value
      : slot.record?.vaccine?.display?.trim()
        ? '-'
        : '';

  // One real <tr> PER dose slot so every column lines up on the same row. The old
  // layout packed all slots as stacked <div>s inside a single <td> per column, so
  // a wrapped Brand Name pushed that column out of sync with the others (the
  // "messy table"). The vaccine name is a single rowSpan cell across the
  // category's slots — matches the printed Vaccination Administration Record.
  return (
    <>
      {slots.map((slot, i) => (
        <tr key={i} className="align-top">
          {i === 0 && (
            <Td rowSpan={slots.length}>
              <span data-vtd="vaccine" className="font-medium text-brand-depth">
                {category.categoryName ?? '—'}
              </span>
            </Td>
          )}
          <Td>{brandName(slot.label, slot.record)}</Td>
          <Td>{cellText(slot, formatDate(slot.record?.administratedDate))}</Td>
          <Td className="whitespace-pre-line">
            {cellText(
              slot,
              slot.record
                ? `${slot.record.lotNo?.trim() || ''}\n${formatDate(slot.record.expiryDate)}`
                : '',
            )}
          </Td>
          <Td>{cellText(slot, slot.record?.site?.display ?? '')}</Td>
          <Td>
            {cellText(
              slot,
              slot.record?.isRx
                ? slot.record?.performerDetails?.name?.trim() ?? ''
                : '',
            )}
          </Td>
          <Td>{cellText(slot, slot.record?.remarks?.trim() ?? '')}</Td>
        </tr>
      ))}
    </>
  );
}

function brandName(
  label: string,
  record: PatientImmunization | null,
): string {
  // Numbered / Other / Unknown prefix + the recorded brand. Flutter
  // shows "1. Pfizer-BioNTech" for the matched dose slot.
  const display = record?.vaccine?.display?.trim();
  if (!label) return display ?? '';
  return display ? `${label} ${display}` : label;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso || iso === 'n/a') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function formatAge(birthDate: string | null | undefined): string {
  if (!birthDate) return '';
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years--;
  return `${years}y`;
}

function Th({ children }: { children: React.ReactNode }) {
  // Title Case (not uppercase) to match the old-system Vaccination
  // Administration Record print — the clinic asked for the legacy look
  // (2026-07-28). The header text is already Title Case in the JSX; the
  // `uppercase` utility was the only reason it rendered ALL-CAPS.
  return (
    <th className="border border-border-default px-3 py-2 text-xs font-bold text-left align-top break-words">
      {children}
    </th>
  );
}

function Td({
  children,
  rowSpan,
  className = '',
}: {
  children: React.ReactNode;
  rowSpan?: number;
  className?: string;
}) {
  return (
    // `break-words` is load-bearing under `table-fixed`: a fixed column can no
    // longer stretch for a long unbroken token (lot numbers, brand codes), so
    // without it the text would overflow the cell border instead of wrapping.
    <td
      rowSpan={rowSpan}
      className={`border border-border-default px-3 py-2 align-top break-words ${className}`}
    >
      {children}
    </td>
  );
}

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <div data-vtd="empty" className="rounded-lg border border-dashed border-border-default p-8 text-center mt-4">
      <p className="text-sm text-text-muted">{children}</p>
    </div>
  );
}
